import Button from "@mui/material/Button";
import "./OverageEntry.css";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import { useFormik } from "formik";
import { OverageEntryFormValue, overageEntryNBSchema } from "../../schema";
import { useDispatch, useSelector } from "react-redux";
import InvestigationForm from "./investigation";
import AppState from "../../AppState";
import { checkPro } from "../../API/checkPro";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { CreateOverageEntryAPI } from "../../API/createOverageEntry";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { useTerminalMaster } from "../../@hooks/useTerminalMaster";
import { usePackageMaster } from "../../@hooks/usePackageMaster";
import Autocomplete from "@mui/material/Autocomplete";
import { UpdateOverageEntryAPI } from "../../API/updateOverageEntry";
import { GetOverageEntryDetailsAPI } from "../../API/getOverageEntryDetailsAPI";
import {
  APP,
  CHECK_DIGIT_FIELDS,
  EFormAction,
  EOsdType,
  EOverageInvestigationStatus,
  FORM_STYLES,
  L_GRID_PROPS,
  M_GRID_PROPS,
  S_GRID_PROPS,
  XL_GRID_PROPS,
} from "../../constants";
import {
  handleKeyDown,
  handleNumberMaxLength,
  handlePaste,
  trimStrings,
  userTerminal,
} from "../../helpers";

import UploadDocument from "../../components/uploadDocument/uploadDocument";
import { printApi } from "../../API/print";
import { IOvEntry } from "../../models/overage";
import { ICheckDigitPro } from "../../models/osd";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { terminalValuesSelector } from "../../AppState/selectors";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import CustomIconComponent from "../../components/CustomDropdownIcon";
import { CommodityField, FormField } from "../../components/shared/form";
import PackageField from "../../components/shared/form/packageField";
import PageHeaderWithBack from "../../components/PageHeaderWithBack";
import { useDateTimeFormatter } from "../../@hooks/useDateTimeFormatter";
import { useAccordionExpand } from "../../@hooks/useAccordionExpand";
import Location from "./Location";
import ClearedInstructionDetails from "../../components/cleared/clearedInstructionDetails";

function OverageEntry() {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const misdeliveredBolPro = new URLSearchParams(location.search).get("msdPro");
  const exceptionNumber = +(params?.osdNumber ?? 0);
  const initValue: IOvEntry = OverageEntryFormValue;
  initValue["reporting"] = userTerminal();

  const { loadTerminals } = useTerminalMaster();
  const { loadPackageOptions } = usePackageMaster();

  // get terminal master from redux state
  let terminalOptions: string[] = useSelector(terminalValuesSelector);

  const packageCodeOptions = useSelector(
    (state: AppState) => state.packageOptionMasterData,
  );

  const [formFieldsDisabled, setFormFieldsDisabled] = React.useState<boolean>(
    Boolean(exceptionNumber),
  );

  const [osdNumber, setOsdNumber] = React.useState<number | undefined>(
    exceptionNumber,
  );
  const [osdDetails, setOsdDetails] = React.useState<IOvEntry>();
  const [investigationStatus, setInvestigationStatus] = React.useState<number>(
    EOverageInvestigationStatus.exceptionEntry,
  );
  const [updatedDateTime, setUpdatedDateTime] = React.useState<string>("");
  const formatShortageDateTime = useDateTimeFormatter(setUpdatedDateTime);

  const [entryUser, setEntryUser] = useState<string>("");
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [proCheckDError, setProCheckDError] = React.useState<boolean>(false);
  // get master from API for commodity code and package code
  const [expanded, setExpanded] = React.useState<string[]>(["panel1"]); // handle expand page using "expanded" variable
  // set Initial value of overage entry form
  // const [initialEditFormValue, setInitialEditFormValue] = React.useState<IOvEntry>();
  const [isEditFormValuePatched, setIsEditFormValuePatched] =
    React.useState<string>("");

  const [initialFormValue, setInitialFormValue] =
    React.useState<IOvEntry>(initValue);
  const [editFormValueChanged, setEditFormValueChanged] =
    React.useState<boolean>(false);
  const isCleared = osdDetails?.clearedInstructionDetails?.cleared;

  const handleFormSubmission = async (values: typeof initValue) => {
    if (!editFormValueChanged && osdNumber) return;
    if (proCheckDError) {
      document.getElementsByName("proNumber")[0].focus();
      return;
    }
    setPageLoading(true);
    let reqInput = prepareRequestPayload(values);
    try {
      if (osdNumber) {
        await handleUpdateEntry(reqInput, values);
      } else {
        await handleCreateEntry(reqInput, values);
      }
    } finally {
      setPageLoading(false);
    }
  };

  // Initialize overage entry form using formik and pass validation schema and initial values
  // handle all form event and submit form using formik
  const formik = useFormik({
    validationSchema: overageEntryNBSchema,
    validateOnChange: false,
    initialValues: initValue,

    // Submit the form if all form fields are validated
    onSubmit: handleFormSubmission,
  });

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    setFieldValue,
    resetForm,
    isSubmitting,
  } = formik;

  const patchedOd400FromMisdeliveryRef = React.useRef(false);

  useEffect(() => {
    if (patchedOd400FromMisdeliveryRef.current) return;
    if (exceptionNumber) return; // don't override edit/view mode

    const bolPro = misdeliveredBolPro?.toString().trim();
    if (!bolPro) return;

    patchedOd400FromMisdeliveryRef.current = true;
    setFieldValue("od400Completed", "Y", false);
    setFieldValue("od400ProNumber", bolPro, false);
  }, [exceptionNumber, misdeliveredBolPro, setFieldValue]);

  // user can print all overage entry form after successfully submitted
  const handlePrint = async () => {
    try {
      setPageLoading(true);
      const printResponse = await printApi(osdNumber?.toString());
      setPageLoading(false);
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          printResponse.status === "ok"
            ? `OS&D # ${osdNumber} Face Sheet was successfully Printed`
            : `OS&D # ${osdNumber} Print Face Sheet was Failed with status ${printResponse.status}`,
          Date.now().toString(),
        ),
      );
    } catch (error) {
      setPageLoading(false);
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string"
            ? error
            : `OS&D # ${osdNumber} Print face sheet failed`,
          Date.now().toString(),
        ),
      );
    }
  };

  const prepareRequestPayload = (values: typeof initValue) => {
    const reqInput = structuredClone(values) as Record<string, any>;
    const deleteFields = ["trailerOrBay"];
    const parseIntFields = [
      "weight",
      "pieces",
      "handlingUnits",
      "od400ProNumber",
      "od400Manifest",
    ];
    deleteFields.push(values.trailerOrBay === "T" ? "bay" : "trailer");
    const valueRange = (reqInput.valueRange ?? "").toString();
    if (valueRange === "1" || valueRange === "2") {
      deleteFields.push("value1k");
    } else {
      parseIntFields.push("value1k");
    }

    if (values.type === "NB") {
      deleteFields.push(
        "proNumber",
        "origin",
        "destination",
        "shipperAccount",
        "consigneeAccount",
      );
    } else {
      parseIntFields.push("proNumber");
    }

    parseIntFields.forEach((f) => {
      reqInput[f] = !Number.isNaN(reqInput[f]) ? Number.parseInt(reqInput[f]) : undefined;
    });

    deleteFields.forEach((f) => delete reqInput[f]);
    reqInput.commodity = reqInput.commodity?.trim();
    reqInput.values = reqInput.values?.toString();
    if(Number.isNaN(reqInput.od400Manifest)){
      reqInput.od400Manifest = 0;
    }
    return trimStrings(reqInput);
  };

  const handleUpdateEntry = async (reqInput: any, values: typeof initValue) => {
    reqInput.username = osdDetails?.username ?? entryUser;
    try {
      await UpdateOverageEntryAPI(reqInput, osdNumber);
      viewOverageEntry(osdNumber);
      setExpanded(["panel1", "panel2"]);
      setFormFieldsDisabled(true);
      setInitialFormValue(values);
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          `OS&D # ${osdNumber} has been updated successfully`,
          Date.now().toString(),
        ),
      );
    } catch (error: any) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string"
            ? error
            : `Failed to update OS&D # ${osdNumber}`,
          Date.now().toString(),
        ),
      );
    }
  };

  const handleCreateEntry = async (reqInput: any, values: typeof initValue) => {
    try {
      const response = await CreateOverageEntryAPI(reqInput);

      if (response?.osdNumber) {
        setInitialFormValue(values);
        setExpanded(["panel1", "panel2"]);
        setFormFieldsDisabled(true);
        viewOverageEntry(response.osdNumber);
        setOsdNumber(response.osdNumber);
        setEntryUser(response.username);
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.SUCCESS,
            `OS&D # ${response.osdNumber} has been created successfully`,
            Date.now().toString(),
          ),
        );
      } else {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            "Overage entry creation failed",
            Date.now().toString(),
          ),
        );
      }
    } catch (error) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : "Overage entry creation failed",
          Date.now().toString(),
        ),
      );
    }
  };

  // get  and package code option master
  const getMasters = (ParamString: string) => {
    if (ParamString === "packageCode") {
      loadPackageOptions();
    } else if (ParamString === "terminalMaster") {
      loadTerminals();
    }
  };

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  // validate pro check digit via API if get pro details proD is valid otherwise not valid and show validation message
  const validateProCheckD = async () => {
    if (!values.proNumber) return;

    const pro = (get(values, "proNumber") ?? "").toString().padStart(11, "0");
    try {
      const checkProResponse = await checkPro(pro);
      if (isEmpty(checkProResponse)) {
        setProCheckDError(true);
      } else {
        setProCheckDError(false);
      }

      patchProDetails(checkProResponse);
    } catch (error) {
      console.error("Error validating PRO check digit:", error);
      patchProDetails({});
      setProCheckDError(true);
    }
  };

  // patch pro details value with OV type got the values from proNumber API as above validateProCheckD method
  const patchProDetails = (checkProResponse: ICheckDigitPro) => {
    const fields: Array<keyof ICheckDigitPro> = CHECK_DIGIT_FIELDS;
    fields.forEach((f: keyof ICheckDigitPro) => {
      setFieldValue(f, checkProResponse[f]?.toString().trim() ?? "");
    });
  };

  const isEntrySaved = (): boolean => {
    return Boolean(osdNumber);
  };

  // set form value on exception type changed
  const reset = (action: EFormAction) => {
    setProCheckDError(false);
    const formValue = structuredClone(values);
    switch (action) {
      case EFormAction.typeChanged:
        if (isEntrySaved()) {
          const fieldsToClear = [
            "shipper",
            "shipperAddress",
            "shipperCity",
            "shipperState",
            "shipperZip",
            "consigneeCity",
            "consigneeState",
            "consigneeZip",
            "consignee",
            "consigneeAddress",
          ];
          if (formValue.type === "NB") {
            fieldsToClear.push(
              "origin",
              "destination",
              "shipperAccount",
              "consigneeAccount",
              "proNumber",
            );
          }
          for (const field of fieldsToClear) {
            setFieldValue(field, "");
          }

          setFieldValue("reporting", formValue.reporting);
        } else {
          resetForm();
          setFieldValue("type", formValue.type);
          if (misdeliveredBolPro) {
            setFieldValue("od400Completed", "Y", false);
            setFieldValue("od400ProNumber", misdeliveredBolPro, false);
          }
        }
        break;
      case EFormAction.create:
        resetForm();
        document.getElementsByName("type")[0].focus();
        break;
      case EFormAction.edit:
        resetForm();
        document.getElementsByName("type")[0].focus();
        patchEditFormValues(initialFormValue);
        break;
    }
  };

  // Handle accordion expand (now using custom hook)
  const handleAccordionChange = useAccordionExpand({
    osdNumber,
    expanded,
    setExpanded,
    setFormFieldsDisabled,
    reset,
  });

  const patchEditFormValues = (overageEntry: any) => {
    APP.OverageEntryFields.forEach((field: string) => {
      const value = overageEntry[field];
      const val = typeof value === "string" ? value.trim() : (value ?? "");
      setFieldValue(field, val);
    });

    const valueRangeMap: Record<string, { value: string; value1k?: any }> = {
      "$0 - $250": { value: "1", value1k: undefined },
      "$250 - $1000": { value: "2", value1k: undefined },
      "GREATER THAN $1000": { value: "3", value1k: overageEntry?.value1k },
    };

    const trimmedValueRange = overageEntry?.valueRange?.trim();
    if (valueRangeMap?.[trimmedValueRange]) {
      setFieldValue("valueRange", valueRangeMap[trimmedValueRange].value);
      setFieldValue("value1k", valueRangeMap[trimmedValueRange].value1k);
    }

    if (overageEntry?.type === "OV") {
      setFieldValue("proNumber", `${overageEntry?.proNumber}`);
    }

    setIsEditFormValuePatched(Date.now().toString());
  };

  const viewOverageEntry = (osdId?: number | string) => {
    if (!osdId) return;
    setPageLoading(true);
    GetOverageEntryDetailsAPI(osdId)
      .then((overageEntry) => {
        if(overageEntry.od400Manifest === 0) {
          overageEntry.od400Manifest = undefined;
        }
        formatShortageDateTime(overageEntry);
        setOsdDetails(overageEntry);
        setInvestigationStatus(
          overageEntry.investigationStatus ??
            EOverageInvestigationStatus.exceptionEntry,
        );
        patchEditFormValues(overageEntry);
      })
      .catch((error) => {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            typeof error === "string" ? error : `OS&D # ${osdNumber} not found`,
            Date.now().toString(),
          ),
        );
      })
      .finally(() => setPageLoading(false));
  };

  const hasError = (field: keyof IOvEntry) => {
    return Boolean(errors[field]) && touched[field];
  };

  const getValue = <T extends keyof IOvEntry>(key: T): string => {
    return (values[key] ?? "").toString();
  };

  // Reusable function to render a FormField with common props
  const renderFormField = ({
    id,
    label,
    type = "text",
    placeholder,
    required = true,
    disabled = formFieldsDisabled,
    maxLength,
    gridProps = S_GRID_PROPS,
    ...rest
  }: {
    id: keyof IOvEntry;
    label: string;
    type?: "number" | "text" | "email";
    placeholder?: string;
    maxLength?: number;
    gridProps?: any;
    required?: boolean;
    [key: string]: any;
  }) => (
    <FormField
      onBlur={handleBlur}
      required={required}
      placeholder={placeholder}
      data-testid={id}
      value={getValue(id)}
      error={hasError(id)}
      disabled={formFieldsDisabled}
      helperText={getHelperText(id)}
      onChange={handleChange}
      id={id}
      type={type}
      label={label}
      maxLength={maxLength}
      gridProps={gridProps}
      {...rest}
    />
  );

  const getErrorMessage = (
    field: keyof IOvEntry,
    className: string = "form-error",
    isCondtional: boolean = true,
  ) => {
    if (errors?.[field] && touched?.[field] && isCondtional) {
      return <span className={className}>{errors?.[field]}</span>;
    }
    return null;
  };

  const getHelperText = (
    field: keyof IOvEntry,
    condtion: boolean = true,
    proCheckDError: boolean = false,
  ) => {
    if (!!errors?.[field] && touched?.[field] && condtion) {
      return errors?.[field];
    } else if (proCheckDError) {
      return "Invalid PRO Check Digit";
    }
    return "";
  };

  const renderDisabledField = (field: any, label: string, md: number = 3) => {
    return (
      <Grid item md={md} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
            {label} <sup>&nbsp;</sup>
          </InputLabel>
          <TextField
            autoComplete="off"
            value={getValue(field)}
            data-testid={field}
            name={field}
            disabled
            variant="outlined"
          />
        </FormControl>
      </Grid>
    );
  };

  useEffect(() => {
    if (isEditFormValuePatched) {
      setInitialFormValue(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFormValuePatched]);

  useEffect(() => {
    setEditFormValueChanged(!isEqual(values, initialFormValue));
    dispatch(isFormValueChanged(!isEqual(values, initialFormValue)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initialFormValue]);

  useEffect(() => {
    dispatch(isFormValueChanged(false));
    if (exceptionNumber) {
      viewOverageEntry(exceptionNumber);
    }
    if (!packageCodeOptions?.length) {
      getMasters("packageCode");
    }
    if (!terminalOptions?.length) {
      getMasters("terminalMaster");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSubmitting) return;
    if (Object.keys(errors).length > 0) {
      document?.getElementsByName(Object.keys(errors)?.[0])?.[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);
  return (
    <div className="bg-layout overage-entry">
      {/* header */}

      <PageHeaderWithBack
        osdNumber={osdNumber}
        proNumber={
          osdDetails?.type === EOsdType.ov
            ? osdDetails?.proNumber
            : osdDetails?.od400ProNumber
        }
        updatedDateTime={updatedDateTime}
        headingTitle="Overage Entry"
        bolPro={misdeliveredBolPro}
      />

      {/* body */}
      <div className="content-area">
        <Accordion
          className="accordion"
          expanded={expanded.includes("panel1")}
          onChange={handleAccordionChange("panel1")}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography
              className={
                isEntrySaved() ? "queue-check queue-check-gc" : "queue-check"
              }
            >
              1
            </Typography>
            <Typography className="accordion-header">Overage Entry</Typography>
          </AccordionSummary>
          <form action="" onSubmit={handleSubmit} autoComplete="off">
            <AccordionDetails className="accordion-container">
              <div id="overageEntry">
                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...S_GRID_PROPS}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        style={FORM_STYLES.LABEL}
                        shrink={true}
                        id="type"
                      >
                        Type <sup>&nbsp;</sup>
                      </InputLabel>
                      <Select
                        disabled={formFieldsDisabled}
                        variant="outlined"
                        id="date"
                        tabIndex={0}
                        autoFocus
                        placeholder="Select a Date"
                        className="select"
                        fullWidth
                        data-testid="type"
                        name="type"
                        value={getValue("type")}
                        onChange={(e) => {
                          reset(EFormAction.typeChanged);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        error={hasError("type")}
                        // onBlur={handleBlur}
                        style={FORM_STYLES.LABEL}
                        IconComponent={CustomIconComponent}
                      >
                        <MenuItem style={FORM_STYLES.LABEL} value="NB">
                          NB
                        </MenuItem>
                        <MenuItem style={FORM_STYLES.LABEL} value="OV">
                          OV
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item {...S_GRID_PROPS}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        style={FORM_STYLES.LABEL}
                        shrink={true}
                        id="type"
                      >
                        Reporting Terminal <sup>*</sup>
                      </InputLabel>
                      <Autocomplete
                        disablePortal
                        disabled={isEntrySaved()}
                        className="select custom-autocomplete"
                        id="reporting-terminal"
                        size="small"
                        disableClearable={true}
                        value={getValue("reporting")}
                        options={terminalOptions}
                        getOptionLabel={(option) => option || ""}
                        onChange={(e, v) => setFieldValue("reporting", v)}
                        noOptionsText={"Records not found"}
                        style={FORM_STYLES.LABEL}
                        popupIcon={
                          <div className="dropdown-icon-group">
                            <ArrowDropUpIcon className="icon-style" />{" "}
                            <ArrowDropDownIcon className="icon-style" />{" "}
                          </div>
                        }
                        renderInput={(params) => (
                          <TextField
                            autoComplete="off"
                            {...params}
                            placeholder="Select Reporting Terminal"
                            variant="outlined"
                            className="dropdown-list"
                            style={FORM_STYLES.LABEL}
                            fullWidth
                            inputProps={{
                              ...params.inputProps,
                              style: { fontSize: "0.9rem" },
                            }}
                          />
                        )}
                      />
                    </FormControl>{" "}
                  </Grid>
                </Grid>

                {/* OV select changes  */}
                {values?.type === "OV" && (
                  <div>
                    <Grid container spacing={2} className="filter-margin">
                      <FormField
                        onBlur={(e) => {
                          validateProCheckD();
                          handleBlur(e);
                        }}
                        type="number"
                        placeholder="Enter Pro Number"
                        data-testid="proNumber"
                        value={getValue("proNumber")}
                        error={
                          (Boolean(errors.proNumber) && touched.proNumber) ||
                          proCheckDError
                        }
                        disabled={formFieldsDisabled}
                        helperText={getHelperText(
                          "proNumber",
                          true,
                          proCheckDError,
                        )}
                        onChange={handleChange}
                        id="proNumber"
                        label="PRO#"
                        maxLength={11}
                      />

                      {renderDisabledField("origin", "Origin Terminal")}
                      {renderDisabledField(
                        "destination",
                        "Destination Terminal",
                      )}
                    </Grid>

                    <Grid container spacing={2} className="filter-margin">
                      {renderDisabledField(
                        "shipperAccount",
                        "Shipper Account #",
                      )}
                      {renderDisabledField("shipper", "Shipper Name")}

                      {renderDisabledField(
                        "shipperAddress",
                        "Shipper Address",
                        6,
                      )}
                    </Grid>

                    <Grid container spacing={2} className="filter-margin">
                      {renderDisabledField("shipperCity", "Shipper City")}
                      {renderDisabledField("shipperState", "Shipper State")}

                      {renderDisabledField("shipperZip", "Shipper Zip")}
                    </Grid>

                    <Grid container spacing={2} className="filter-margin">
                      {renderDisabledField(
                        "consigneeAccount",
                        "Consignee Account #",
                      )}
                      {renderDisabledField("consignee", "Consignee Name")}

                      {renderDisabledField(
                        "consigneeAddress",
                        "Consignee Address",
                        6,
                      )}
                    </Grid>

                    <Grid container spacing={2} className="filter-margin">
                      {renderDisabledField("consigneeCity", "Consignee City")}
                      {renderDisabledField("consigneeState", "Consignee State")}
                      {renderDisabledField("consigneeZip", "Consignee Zip")}
                    </Grid>
                  </div>
                )}

                {/* NB select changes  */}

                {values?.type === "NB" && (
                  <>
                    <Grid container spacing={2} className="filter-margin">
                      {renderFormField({
                        id: "shipper",
                        label: "Shipper Name",
                        maxLength: 30,
                        placeholder: "Enter Shipper Name",
                      })}
                      {renderFormField({
                        id: "shipperAddress",
                        label: "Shipper Address",
                        maxLength: 30,
                        placeholder: "Enter Shipper Address",
                        gridProps: L_GRID_PROPS,
                      })}
                    </Grid>
                    <Grid container spacing={2} className="filter-margin">
                      <Location
                        osdDetails={osdDetails}
                        formFieldsDisabled={formFieldsDisabled}
                        formik={formik}
                        key={formik.values.shipperState}
                        formObject={{
                          cityField: "shipperCity",
                          zipField: "shipperZip",
                          cityLabel: "Shipper City",
                          zipLabel: "Shipper Zip",
                          stateField: "shipperState",
                          stateLabel: "Shipper State",
                        }}
                      />
                    </Grid>

                    <Grid container spacing={2} className="filter-margin">
                      {renderFormField({
                        id: "consignee",
                        label: "Consignee Name",
                        maxLength: 30,
                        placeholder: "Enter Consignee Name",
                      })}
                      {renderFormField({
                        id: "consigneeAddress",
                        label: "Consignee Address",
                        maxLength: 30,
                        placeholder: "Enter Consignee Address",
                        gridProps: L_GRID_PROPS,
                      })}
                    </Grid>
                    <Grid container spacing={2} className="filter-margin">
                      <Location
                        osdDetails={osdDetails}
                        formFieldsDisabled={formFieldsDisabled}
                        formik={formik}
                        key={formik.values.consigneeState}
                        formObject={{
                          cityField: "consigneeCity",
                          zipField: "consigneeZip",
                          cityLabel: "Consignee City",
                          zipLabel: "Consignee Zip",
                          stateField: "consigneeState",
                          stateLabel: "Consignee State",
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...S_GRID_PROPS}>
                    <FormControl className="radio-group">
                      <FormLabel id="od400Bill">
                        Was an OD400 Bill Created? <sup>*</sup>
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="od400Bill"
                        data-testid="od400Completed"
                        name="od400Completed"
                      >
                        <FormControlLabel
                          value="Y"
                          control={<Radio />}
                          label="Yes"
                          name="od400Completed"
                          onChange={handleChange}
                          disabled={formFieldsDisabled}
                          onBlur={handleBlur}
                          checked={values.od400Completed === "Y"}
                        />
                        <FormControlLabel
                          value="N"
                          control={<Radio />}
                          label="No"
                          name="od400Completed"
                          onChange={(e) => {
                            setFieldValue("od400ProNumber", "", true);
                            handleChange(e);
                          }}
                          disabled={formFieldsDisabled}
                          onBlur={handleBlur}
                          checked={values.od400Completed === "N"}
                        />
                      </RadioGroup>

                      {values.od400Completed === "N" ? (
                        <span className="form-error">
                          Cannot proceed until OD400 bill is created
                        </span>
                      ) : (
                        getErrorMessage("od400Completed")
                      )}
                    </FormControl>
                  </Grid>

                  <FormField
                    id="od400ProNumber"
                    label="OD400 PRO#"
                    type="number"
                    required={true}
                    value={values.od400ProNumber}
                    placeholder="Enter OD400 PRO#"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={hasError("od400ProNumber")}
                    disabled={
                      values.od400Completed !== "Y" || formFieldsDisabled
                    }
                    helperText={getHelperText(
                      "od400ProNumber",
                      values.od400Completed === "Y",
                    )}
                    maxLength={11}
                  />

                  {renderFormField({
                    id: "od400Manifest",
                    type: "number",
                    label: "If located on Manifest, Provide Manifest #",
                    placeholder: "Enter Manifest Number",
                    maxLength: 9,
                    required: false,
                  })}
                </Grid>

                <Grid container spacing={2} className="">
                  <Grid item {...M_GRID_PROPS}>
                    <FormControl className="min-width checkbox-group">
                      <FormLabel id="shipmentLocation">
                        Was Shipment Located On<sup>*</sup>
                      </FormLabel>

                      <Grid container spacing={2}>
                        <Grid item {...M_GRID_PROPS} className="p10">
                          <FormGroup className="form-row">
                            <FormControlLabel
                              value="T"
                              control={<Radio />}
                              label="Trailer"
                              data-testid="trailerOrBay"
                              name="trailerOrBay"
                              onChange={handleChange}
                              disabled={formFieldsDisabled}
                              onBlur={handleBlur}
                              className="pull-right-label"
                              checked={values.trailerOrBay === "T"}
                            />
                            <FormControlLabel
                              value="B"
                              control={<Radio />}
                              label="Bay"
                              data-testid="trailerOrBay"
                              name="trailerOrBay"
                              onChange={handleChange}
                              disabled={formFieldsDisabled}
                              onBlur={handleBlur}
                              checked={values.trailerOrBay === "B"}
                              className="pull-right-label"
                            />
                          </FormGroup>
                        </Grid>

                        <Grid item {...M_GRID_PROPS} className="pt-6">
                          <FormGroup className="form-row">
                            <FormGroup className="form-validation">
                              {values.trailerOrBay === "T" && (
                                <>
                                  <TextField
                                    autoComplete="off"
                                    className="justify-content"
                                    id="outlined-basic"
                                    placeholder="Enter Trailer Number"
                                    variant="outlined"
                                    onChange={handleChange}
                                    error={hasError("trailer")}
                                    disabled={formFieldsDisabled}
                                    onBlur={handleBlur}
                                    data-testid="trailer"
                                    name="trailer"
                                    inputProps={{ maxLength: 15 }}
                                    value={getValue("trailer")}
                                  />
                                  {getErrorMessage("trailer")}
                                </>
                              )}
                              {values.trailerOrBay === "B" && (
                                <>
                                  <TextField
                                    autoComplete="off"
                                    className="justify-content"
                                    id="outlined-basic"
                                    placeholder="Enter Bay Number"
                                    variant="outlined"
                                    onChange={handleChange}
                                    error={hasError("bay")}
                                    disabled={formFieldsDisabled}
                                    onBlur={handleBlur}
                                    data-testid="bay"
                                    name="bay"
                                    value={getValue("bay")}
                                    inputProps={{ maxLength: 4 }}
                                  />
                                  {getErrorMessage("bay")}
                                </>
                              )}
                            </FormGroup>
                          </FormGroup>
                        </Grid>

                        {getErrorMessage(
                          "trailerOrBay",
                          "form-error checkbox-group-validation",
                        )}
                      </Grid>
                    </FormControl>
                  </Grid>
                  <Grid item {...S_GRID_PROPS}>
                    <FormControl
                      fullWidth
                      size="medium"
                      className="radio-group value-range"
                    >
                      <FormLabel
                        className="mt-0 mb-10 col-md-12"
                        id="exceptionLocation"
                      >
                        Current Location <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="Specify the current location for this exception, e.g., OSDbay or TRL#."
                          tooltipKey="tooltip1"
                        />
                      </FormLabel>

                      <TextField
                        autoComplete="off"
                        id="exceptionLocation"
                        placeholder="Enter current location"
                        variant="outlined"
                        onChange={handleChange}
                        error={hasError("exceptionLocation")}
                        disabled={formFieldsDisabled}
                        onBlur={handleBlur}
                        data-testid="exceptionLocation"
                        name="exceptionLocation"
                        value={getValue("exceptionLocation")}
                        inputProps={{ maxLength: 15 }}
                      />
                    </FormControl>

                    {getErrorMessage("exceptionLocation")}
                  </Grid>
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  <Grid item {...L_GRID_PROPS}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        style={FORM_STYLES.LABEL}
                        shrink={true}
                        id="shipmentHave"
                      >
                        Does Shipment Have<sup>&nbsp;</sup>
                      </InputLabel>

                      <Grid container spacing={2}>
                        <Grid item md={4} xs={12}>
                          <FormGroup className="form-row">
                            <TextField
                              autoComplete="off"
                              className="justify-content disabled"
                              value="BL#"
                              variant="outlined"
                              disabled
                            />

                            <TextField
                              autoComplete="off"
                              className="justify-content"
                              placeholder="Enter BL Number"
                              variant="outlined"
                              onChange={handleChange}
                              disabled={formFieldsDisabled}
                              error={hasError("billNumber")}
                              data-testid="billNumber"
                              name="billNumber"
                              value={getValue("billNumber")}
                              inputProps={{ maxLength: 20 }}
                            />
                          </FormGroup>
                          {getErrorMessage("billNumber")}
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <FormGroup className="form-row">
                            <TextField
                              autoComplete="off"
                              className="justify-content disabled"
                              value="PO#"
                              variant="outlined"
                              disabled
                            />

                            <TextField
                              autoComplete="off"
                              className="justify-content"
                              placeholder="Enter PO Number"
                              variant="outlined"
                              onChange={handleChange}
                              disabled={formFieldsDisabled}
                              error={hasError("poNumber")}
                              data-testid="poNumber"
                              name="poNumber"
                              value={getValue("poNumber")}
                              inputProps={{ maxLength: 20 }}
                            />
                          </FormGroup>
                          {getErrorMessage("poNumber")}
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <FormGroup className="form-row">
                            <TextField
                              autoComplete="off"
                              className="justify-content disabled"
                              value="SN#"
                              variant="outlined"
                              disabled
                            />

                            <TextField
                              autoComplete="off"
                              className="justify-content"
                              placeholder="Enter SN Number"
                              variant="outlined"
                              onChange={handleChange}
                              disabled={formFieldsDisabled}
                              error={hasError("snNumber")}
                              data-testid="snNumber"
                              name="snNumber"
                              value={getValue("snNumber")}
                              inputProps={{ maxLength: 20 }}
                            />
                          </FormGroup>
                          {getErrorMessage("snNumber")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item {...S_GRID_PROPS}>
                    <FormControl className="radio-group">
                      <FormLabel id="hazmat">
                        Haz Mat <sup>&nbsp;</sup>
                      </FormLabel>
                      <RadioGroup
                        data-testid="hazmat"
                        row
                        aria-labelledby="hazmat"
                        name="hazmat"
                      >
                        <FormControlLabel
                          value="Y"
                          control={<Radio />}
                          label="Yes"
                          name="hazmat"
                          onChange={handleChange}
                          disabled={formFieldsDisabled}
                          onBlur={handleBlur}
                          checked={values.hazmat === "Y"}
                        />
                        <FormControlLabel
                          value="N"
                          control={<Radio />}
                          label="No"
                          name="hazmat"
                          onChange={handleChange}
                          disabled={formFieldsDisabled}
                          onBlur={handleBlur}
                          checked={values.hazmat === "N"}
                        />
                      </RadioGroup>
                      {getErrorMessage("hazmat")}
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  {renderFormField({
                    id: "handlingUnits",
                    label: "How Many Handling Units Located",
                    type: "number",
                    placeholder: "Enter Units Number",
                    maxLength: 5,
                  })}

                  <CommodityField
                    key={values.commodity}
                    id="commodity"
                    label="Commodity"
                    disabled={formFieldsDisabled}
                    value={values.commodity}
                    onChange={(_, value) =>
                      setFieldValue("commodity", value?.code)
                    }
                    placeholder="Enter commodity"
                    required
                    onBlur={handleBlur("commodity")}
                    error={touched.commodity && !!errors.commodity}
                    helperText={getHelperText("commodity", !values.commodity)}
                  />

                  <PackageField
                    formFieldsDisabled={formFieldsDisabled}
                    value={values?.package ?? ""}
                    handleChange={(event) => {
                      setFieldValue("package", event.target.value);
                    }}
                    handleBlur={handleBlur("package")}
                    error={(
                      getHelperText("package", !values.package) ?? ""
                    ).toString()}
                    hasError={(field, condition) =>
                      !!touched[field as keyof typeof touched] && condition
                    }
                  />
                  {renderFormField({
                    id: "pieces",
                    label: "Pieces",
                    type: "number",
                    placeholder: "Enter Pieces",
                    maxLength: 5,
                  })}
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  {renderFormField({
                    id: "weight",
                    label: "Weight",
                    type: "number",
                    placeholder: "Enter Weight",
                    maxLength: 7,
                  })}

                  <Grid item {...S_GRID_PROPS}>
                    <FormControl className="radio-group">
                      <FormLabel id="shrinkWrap">
                        Was Shipment Shrink Wrapped? <sup>*</sup>
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="shrinkWrap"
                        data-testid="shrinkWrap"
                        name="shrinkWrap"
                      >
                        <FormControlLabel
                          value="Y"
                          control={<Radio />}
                          label="Yes"
                          name="shrinkWrap"
                          onChange={handleChange}
                          disabled={formFieldsDisabled}
                          onBlur={handleBlur}
                          checked={values.shrinkWrap === "Y"}
                        />
                        <FormControlLabel
                          value="N"
                          control={<Radio />}
                          label="No"
                          name="shrinkWrap"
                          onChange={handleChange}
                          disabled={formFieldsDisabled}
                          onBlur={handleBlur}
                          checked={values.shrinkWrap === "N"}
                        />
                      </RadioGroup>
                      {getErrorMessage("shrinkWrap")}
                    </FormControl>
                  </Grid>

                  {renderFormField({
                    id: "itemCartonModelSerialNumber",
                    label:
                      "Does the shipment have (Item#, Carton#, Model#, Serial#)",
                    maxLength: 56,
                    placeholder:
                      "Enter Item#, Carton#, Model#, Serial# with comma separated",
                    gridProps: M_GRID_PROPS,
                  })}
                </Grid>
                <Grid
                  container
                  spacing={2}
                  className="filter-margin align-items"
                >
                  <Grid item {...L_GRID_PROPS}>
                    <FormControl className="radio-group value-range">
                      <FormLabel id="valueRange">
                        Value Range <sup>*</sup>
                      </FormLabel>

                      <Grid container spacing={2}>
                        <Grid
                          item
                          {...XL_GRID_PROPS}
                          className="float-div form-content-start"
                        >
                          <FormGroup className="shift-left">
                            <RadioGroup
                              row
                              aria-labelledby="valueRange"
                              data-testid="valueRange"
                              name="valueRange"
                              value={getValue("valueRange")}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <FormControlLabel
                                value="1"
                                disabled={isEntrySaved()}
                                control={<Radio />}
                                label="$0 - $250.00"
                                name="valueRange"
                                onChange={handleChange}
                                checked={values.valueRange === "1"}
                              />
                              <FormControlLabel
                                value="2"
                                disabled={isEntrySaved()}
                                control={<Radio />}
                                label="$250.00 - $1,000.00"
                                name="valueRange"
                                onChange={handleChange}
                                checked={values.valueRange === "2"}
                              />
                              <FormControlLabel
                                disabled={isEntrySaved()}
                                value="3"
                                control={<Radio />}
                                label="Greater than $1,000.00"
                                name="valueRange"
                                onChange={handleChange}
                                checked={values.valueRange === "3"}
                              />
                              <TooltipWithClickAway
                                tooltipText="Round the value up to the nearest dollar amount"
                                tooltipKey="tooltip1"
                              />
                            </RadioGroup>
                          </FormGroup>

                          {values.valueRange === "3" && (
                            <FormGroup className="no-wrap">
                              <FormGroup className="form-row shift-left">
                                <TextField
                                  autoComplete="off"
                                  className="justify-content disabled"
                                  value="$"
                                  variant="outlined"
                                  disabled
                                />
                                <TextField
                                  autoComplete="off"
                                  type="number"
                                  onPaste={handlePaste}
                                  className="justify-content"
                                  placeholder="Enter Amount"
                                  variant="outlined"
                                  onChange={handleChange}
                                  error={hasError("value1k")}
                                  disabled={isEntrySaved()}
                                  onBlur={handleBlur}
                                  data-testid="value1k"
                                  name="value1k"
                                  value={getValue("value1k")}
                                  onKeyDown={handleKeyDown}
                                  onInput={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    handleNumberMaxLength(e, 10);
                                  }}
                                />
                              </FormGroup>
                              {getErrorMessage("value1k")}
                            </FormGroup>
                          )}
                        </Grid>
                        {getErrorMessage(
                          "valueRange",
                          "form-error radio-group-validation",
                        )}
                      </Grid>
                    </FormControl>
                  </Grid>
                  {renderFormField({
                    id: "manufacturerBrand",
                    label: "Who Is The Manufacturer And/Or Brand",
                    maxLength: 30,
                    placeholder: "Manufacturer And/Or Brand",
                    required: false,
                  })}
                </Grid>

                <h3 className="accordion-content-header">Additional Details</h3>

                <Grid container spacing={2} className="filter-margin">
                  {renderFormField({
                    id: "dimension",
                    label: "Exact Dimensions/Measurements",
                    maxLength: 75,
                    placeholder: "Enter Dimensions/Measurements",
                  })}
                  {renderFormField({
                    id: "barcode",
                    label: "Barcode",
                    maxLength: 75,
                    placeholder: "Enter Barcode",
                    required: false,
                  })}
                  {renderFormField({
                    id: "colorCode",
                    label: "Color Coding",
                    maxLength: 75,
                    placeholder: "Enter Color Codes",
                  })}
                  {renderFormField({
                    id: "heatNumbers",
                    label: "Heat Numbers",
                    maxLength: 75,
                    placeholder: "Enter Heat Numbers",
                    required: false,
                  })}
                </Grid>

                <Grid container spacing={2} className="filter-margin">
                  {renderFormField({
                    id: "agentInterline",
                    label: "Agent/Interline Info And/Or Pro",
                    maxLength: 75,
                    placeholder: "Enter Agent/Interline Info And/Or Pro",
                    required: false,
                  })}
                  <Grid item {...L_GRID_PROPS}>
                    {renderFormField({
                      id: "additionalDescription",
                      label: "Description",
                      placeholder: "Enter Additional Description",
                      // maxLength: 350, // Uncomment if you want to enforce max length
                      gridProps: XL_GRID_PROPS,
                    })}
                  </Grid>
                </Grid>
              </div>
              {!isCleared && (
                <div className="btn-group-submit">
                  <Button
                    data-testid="print-face-sheet-button"
                    variant="contained"
                    onClick={() => {
                      osdNumber ? handlePrint() : void 0;
                    }}
                    className={
                      osdNumber ? "entry" : "disable-btn disable entry"
                    }
                  >
                    Print Face Sheet
                  </Button>
                  {Boolean(osdNumber) && !formFieldsDisabled && (
                    <>
                      <Button
                        type="button"
                        variant="contained"
                        className="clear-btn mr-0 ml"
                        onClick={() => reset(EFormAction.edit)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        data-testid="update-button"
                        className={
                          editFormValueChanged
                            ? "assign mr-0 ml"
                            : "disable-btn disable assign mr-0 ml"
                        }
                      >
                        Update
                      </Button>
                    </>
                  )}
                  {!osdNumber && (
                    <>
                      <Button
                        type="button"
                        variant="contained"
                        className="clear-btn mr-0 ml"
                        onClick={() => reset(EFormAction.create)}
                      >
                        Clear
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        data-testid="testid-ov-submit-button"
                        className="entry ml"
                      >
                        {values.valueRange === "3"
                          ? "Submit & Send APB"
                          : "Submit"}
                      </Button>
                    </>
                  )}
                  {Boolean(osdNumber) &&
                    formFieldsDisabled &&
                    investigationStatus !==
                      EOverageInvestigationStatus.complete && (
                      <Button
                        // type="submit"
                        variant="contained"
                        className="assign mr-0 ml"
                        onClick={enableEditing}
                        data-testid="testid-ov-edit-button"
                      >
                        Edit
                      </Button>
                    )}
                </div>
              )}
            </AccordionDetails>
          </form>
        </Accordion>
        <Accordion
          className="accordion"
          expanded={expanded.includes("panel2")}
          onChange={handleAccordionChange("panel2")}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2bh-content"
            id="panel2bh-header"
          >
            <Typography
              className={
                investigationStatus === EOverageInvestigationStatus.complete || isCleared
                  ? "queue-check queue-check-gc"
                  : "queue-check"
              }
            >
              2
            </Typography>
            <Typography className="accordion-header mr-15">
              Overage Investigation &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Typography>

            <Typography className="header-notice note-scroll">
              <div className="animate">
                Note: Do not advise the shipper or consignee that we have
                overage freight
              </div>
            </Typography>
          </AccordionSummary>
          <InvestigationForm
            key={values.type}
            osdNumber={osdNumber}
            expanded={expanded}
            exceptionType={values.type}
            proNumber={values?.proNumber}
            investigationDetails={osdDetails}
            isEditMode={Boolean(exceptionNumber)}
            setInvestigationStatus={setInvestigationStatus}
            isCleared={isCleared}
          />
        </Accordion>

        <Accordion
          className="accordion"
          expanded={expanded.includes("panel3")}
          onChange={handleAccordionChange("panel3")}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3bh-content"
            id="panel3bh-header"
          >
            <Typography className={isCleared? "queue-check queue-check-gc" : "queue-check"}>3</Typography>
            <Typography className="accordion-header">
              {isCleared ? "Cleared Instruction Details" : "Upload Documents"}
            </Typography>
          </AccordionSummary>

          {isCleared ? (
            <ClearedInstructionDetails
              clearedInstructionDetails={osdDetails?.clearedInstructionDetails}
            />
          ) : (
            <UploadDocument
              key={expanded.toString() || "upload-document"}
              osdNumber={osdNumber}
            />
          )}
        </Accordion>
      </div>
      <Loader pageLoader={pageLoading}></Loader>
    </div>
  );
}

export default OverageEntry;
